import { type ReactElement } from 'react'
import { useRequiredParams } from '@util'
import { type GameId, type WithGameId } from '@terros/common'
import { createFileRoute } from '@tanstack/react-router'
import { useGame } from '@front/model'
import { NestedNavigation, NotReady, type PageRoute } from '@components'
import { GameDataContext } from './useGameContext'
import { GameHeaderProvider } from './gameHeaderContext'
import { CompetitionHeading } from './CompetitionImage'

export type CompetitionPage = 'participant' | 'scoring' | 'prize' | 'marketing' | 'timeline' | 'summary' | 'bracket'

type CompetitionPageRoute = Omit<PageRoute, 'path'> & {
  path: `/competition/${GameId}/${CompetitionPage}` | '/competition'
}

function Competition(): ReactElement {
  const { gameId } = useRequiredParams<WithGameId>()

  const { data, isLoading, error: getError, refetch, isFetching } = useGame({ gameId })

  if (isLoading || !data || getError) {
    return <NotReady data={data} error={getError} isLoading={isLoading} />
  }

  const routes: CompetitionPageRoute[] = [
    { path: `/competition/${gameId}/participant`, label: 'Participants', icon: 'groups' },
    { path: `/competition/${gameId}/scoring`, label: 'Scoring', icon: 'numbers' },
    { path: `/competition/${gameId}/prize`, label: 'Prizes', icon: 'emoji_events' },
    { path: `/competition/${gameId}/timeline`, label: 'Timeline', icon: 'calendar_month' },
    { path: `/competition/${gameId}/marketing`, label: 'Marketing', icon: 'campaign' },
    { path: `/competition/${gameId}/summary`, label: 'Summary', icon: 'table' },
  ]

  if (data.game.gameType === 'bracket') {
    routes.splice(5, 0, { path: `/competition/${gameId}/bracket`, label: 'Bracket', icon: 'data_array' })
  }

  return (
    <GameDataContext.Provider value={{ data, refetch, isFetching }}>
      <GameHeaderProvider initialGame={data.game}>
        <div className='flex flex-col'>
          <CompetitionHeading />
          <NestedNavigation routes={routes} />
        </div>
      </GameHeaderProvider>
    </GameDataContext.Provider>
  )
}

export const Route = createFileRoute('/competition_/$gameId')({
  component: Competition,
})
